<script lang="ts">
    /**
     * Five admin views over the consultant delivery and the app's own contacts.
     *
     * Each section loads and fails independently, so one broken endpoint does
     * not blank the page. load() is exported because Svelte 4's onMount does not
     * fire in this project's jsdom test environment (see Map.test.ts), and it
     * coalesces concurrent calls so the store subscriptions below and a test's
     * awaited call resolve on one request per endpoint.
     */
    import { onDestroy } from 'svelte';
    import { get } from 'svelte/store';
    import { push } from 'svelte-spa-router';
    import { authReady, currentUser } from '$lib/stores/auth';
    import {
        getActivity,
        getOutreachPrecinct,
        getPreferenceAge,
        getPrecinctScorecard,
        getPropensityParty,
        humanizeError,
        type ActivityResponse,
        type OutreachPrecinctResponse,
        type PreferenceAgeResponse,
        type PrecinctScorecardResponse,
        type PropensityPartyResponse,
    } from '$lib/api';
    import {
        colorFrom,
        labelAgeRange,
        labelBucket,
        labelPartyPreference,
        labelVotingPreference,
        orderBuckets,
        orderKeys,
        orderPivot,
        percentOf,
        pivot,
        pivotWeeks,
        sortPrecinctsByOptedInShare,
        weekLabel,
        AGE_RANGE_ORDER,
        CONTACT_RESULT_COLORS,
        CONTACT_RESULT_LABELS,
        CONTACT_RESULT_ORDER,
        PARTY_PREFERENCE_COLORS,
        PARTY_PREFERENCE_ORDER,
        VOTING_PREFERENCE_COLORS,
        VOTING_PREFERENCE_ORDER,
    } from '$lib/charts';
    import {
        outreachColor,
        outreachLabel,
        OUTREACH_PRECEDENCE,
        type TextOutreach,
    } from '$lib/outreach';
    import AdminNav from '../components/AdminNav.svelte';
    import SortableTable from '../components/charts/SortableTable.svelte';
    import StackedBars from '../components/charts/StackedBars.svelte';

    const EMPTY = 'No consultant delivery imported yet. Use Data deliveries.';

    let propensity: PropensityPartyResponse | null = null;
    let propensityError: string | null = null;
    let outreach: OutreachPrecinctResponse | null = null;
    let outreachError: string | null = null;
    let scorecard: PrecinctScorecardResponse | null = null;
    let scorecardError: string | null = null;
    let preference: PreferenceAgeResponse | null = null;
    let preferenceError: string | null = null;
    let activity: ActivityResponse | null = null;
    let activityError: string | null = null;

    let inflight: Promise<void> | null = null;

    export function load(): Promise<void> {
        // Wait for the initial /api/me before caching anything. authReady and
        // currentUser both emit synchronously inside initAuth's continuation
        // (lib/stores/auth.ts), so on a fresh mount the currentUser subscription
        // fires first, while authReady is still false. If that no-op run were
        // allowed into `inflight`, the authReady subscription's call (moments
        // later, still synchronous) would return the same already-resolved
        // promise instead of running the real load, since `inflight` is only
        // cleared on a later microtask by `.finally()`. Gating here, before the
        // coalescing, means only a run that can actually fetch or redirect ever
        // occupies `inflight`.
        if (!get(authReady)) return Promise.resolve();
        if (!inflight) {
            inflight = runLoad().finally(() => {
                inflight = null;
            });
        }
        return inflight;
    }

    async function runLoad(): Promise<void> {
        if (!$currentUser || $currentUser.role !== 'admin') {
            push('/map');
            return;
        }
        propensityError = null;
        outreachError = null;
        scorecardError = null;
        preferenceError = null;
        activityError = null;
        await Promise.all([
            getPropensityParty()
                .then(r => (propensity = r))
                .catch(e => {
                    propensityError = humanizeError(e, 'Failed to load propensity data');
                }),
            getOutreachPrecinct()
                .then(r => (outreach = r))
                .catch(e => {
                    outreachError = humanizeError(e, 'Failed to load text outreach data');
                }),
            getPrecinctScorecard()
                .then(r => (scorecard = r))
                .catch(e => {
                    scorecardError = humanizeError(e, 'Failed to load the precinct scorecard');
                }),
            getPreferenceAge()
                .then(r => (preference = r))
                .catch(e => {
                    preferenceError = humanizeError(e, 'Failed to load voting preference data');
                }),
            getActivity()
                .then(r => (activity = r))
                .catch(e => {
                    activityError = humanizeError(e, 'Failed to load canvass activity');
                }),
        ]);
    }

    const unsubUser = currentUser.subscribe(() => {
        void load();
    });
    const unsubReady = authReady.subscribe(() => {
        void load();
    });
    onDestroy(() => {
        unsubUser();
        unsubReady();
    });

    function asOf(iso: string | null): string {
        const d = iso ? new Date(iso) : new Date();
        // received_at is not a real instant: the backend sets it to 00:00 UTC of the
        // delivery filename's date (see consultant/normalize.py). Format it in UTC so
        // the displayed day matches the filename regardless of viewer timezone. The
        // null case (chart5's "now") has no such stamp, so it stays in the viewer's zone.
        return d.toLocaleDateString(undefined, {
            timeZone: iso ? 'UTC' : undefined,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }

    $: chart1 =
        propensity && propensity.delivery
            ? orderPivot(pivot(propensity.rows, 'propensity_bucket', 'party_preference', 'count'), {
                  categoryOrder: orderBuckets,
                  categoryLabel: labelBucket,
                  seriesOrder: PARTY_PREFERENCE_ORDER,
                  seriesLabel: labelPartyPreference,
                  colorOf: colorFrom(PARTY_PREFERENCE_COLORS),
              })
            : null;

    $: chart2 =
        outreach && outreach.delivery
            ? orderPivot(pivot(outreach.rows, 'precinct', 'state', 'count'), {
                  categoryOrder: () => sortPrecinctsByOptedInShare(outreach?.rows ?? []),
                  seriesOrder: OUTREACH_PRECEDENCE,
                  // orderPivot hands its seriesLabel a raw string key, and
                  // outreachLabel takes the state union, so cast at the seam.
                  seriesLabel: (k: string) => outreachLabel(k as TextOutreach),
                  colorOf: outreachColor,
              })
            : null;

    $: chart4 =
        preference && preference.delivery
            ? orderPivot(pivot(preference.rows, 'age_range', 'voting_preference', 'count'), {
                  categoryOrder: keys => orderKeys(keys, AGE_RANGE_ORDER),
                  categoryLabel: labelAgeRange,
                  seriesOrder: VOTING_PREFERENCE_ORDER,
                  seriesLabel: labelVotingPreference,
                  colorOf: colorFrom(VOTING_PREFERENCE_COLORS),
              })
            : null;

    $: chart5 = activity
        ? orderPivot(pivotWeeks(activity.weeks), {
              categoryLabel: weekLabel,
              seriesOrder: CONTACT_RESULT_ORDER,
              seriesLabel: k => CONTACT_RESULT_LABELS[k] ?? k,
              colorOf: colorFrom(CONTACT_RESULT_COLORS),
          })
        : null;

    $: noContacts =
        !!activity && activity.weeks.every(w => Object.keys(w.results).length === 0);

    // The two percentage columns are computed here, not in SQL: the denominators
    // are already in the row. Headers name the denominator so a coverage rate is
    // never read as a conversion rate.
    const SCORECARD_COLUMNS = [
        { key: 'precinct', label: 'Precinct' },
        { key: 'registered', label: 'Registered', numeric: true, bar: true },
        { key: 'active', label: 'Active', numeric: true },
        { key: 'on_target_list', label: 'On target list', numeric: true, bar: true },
        { key: 'text_contacted', label: 'Text contacted', numeric: true },
        { key: 'text_contacted_pct', label: 'Text contacted % of registered', numeric: true },
        { key: 'opted_in', label: 'Opted in', numeric: true },
        { key: 'door_contacts', label: 'Door contacts', numeric: true, bar: true },
        { key: 'spoke', label: 'Spoke', numeric: true },
        { key: 'spoke_pct', label: 'Spoke % of door contacts', numeric: true },
        { key: 'avg_support', label: 'Avg support', numeric: true },
    ];

    const ISSUE_COLUMNS = [
        { key: 'display_name', label: 'Issue' },
        { key: 'count', label: 'Contacts', numeric: true, bar: true },
    ];

    $: scorecardRows = (scorecard?.rows ?? []).map(
        (r): Record<string, string | number | null> => ({
            ...r,
            text_contacted_pct: percentOf(r.text_contacted, r.registered),
            spoke_pct: percentOf(r.spoke, r.door_contacts),
        }),
    );

    // Server order is the display order: the backend returns the newest
    // WRITE_IN_LIMIT write-ins, so re-sorting here would present a truncated
    // slice as if it were the whole set.
    $: writeIns = activity?.write_ins ?? { total: 0, rows: [] };
    $: writeInsHidden = writeIns.total - writeIns.rows.length;

    $: issueRows = (activity?.issues ?? []).map(
        (i): Record<string, string | number | null> => ({
            display_name: i.display_name,
            count: i.count,
        }),
    );
</script>

<div class="admin-page">
    <header class="admin-header">
        <span class="eyebrow">Admin, Dashboards</span>
        <h1>Campaign dashboards</h1>
        <p class="page-sub">Consultant delivery views and canvass activity.</p>
    </header>

    <AdminNav active="dashboards" />

    <section class="block">
        <div class="block-head"><h2>Turnout propensity by party preference</h2></div>
        {#if propensityError}
            <p class="msg msg-error">{propensityError}</p>
        {:else if !propensity}
            <p class="loading">Loading...</p>
        {:else if !propensity.delivery}
            <p class="empty">{EMPTY}</p>
        {:else}
            <p class="as-of">
                Data as of {asOf(propensity.delivery.received_at)}. Source: {propensity.delivery
                    .filename}, the whole district file rather than only this app's voters.
            </p>
            {#if chart1 && chart1.categories.length}
                <StackedBars
                    categories={chart1.categories}
                    series={chart1.series}
                    orientation="vertical"
                    valueLabel="Voters"
                    title="Turnout propensity by party preference" />
            {:else}
                <p class="empty">This delivery has no rows.</p>
            {/if}
        {/if}
    </section>

    <section class="block">
        <div class="block-head"><h2>Text outreach by precinct</h2></div>
        {#if outreachError}
            <p class="msg msg-error">{outreachError}</p>
        {:else if !outreach}
            <p class="loading">Loading...</p>
        {:else if !outreach.delivery}
            <p class="empty">{EMPTY}</p>
        {:else}
            <p class="as-of">
                Data as of {asOf(outreach.delivery.received_at)}. Precincts sorted by opted-in
                share. Source: the delivery's own precinct field, so totals do not match the
                scorecard below.
            </p>
            {#if chart2 && chart2.categories.length}
                <StackedBars
                    categories={chart2.categories}
                    series={chart2.series}
                    orientation="horizontal"
                    valueLabel="Voters"
                    title="Text outreach by precinct" />
            {:else}
                <p class="empty">This delivery has no rows.</p>
            {/if}
        {/if}
    </section>

    <section class="block" aria-labelledby="scorecard-heading">
        <div class="block-head"><h2 id="scorecard-heading">Precinct scorecard</h2></div>
        {#if scorecardError}
            <p class="msg msg-error">{scorecardError}</p>
        {:else if !scorecard}
            <p class="loading">Loading...</p>
        {:else}
            <p class="as-of">
                {#if scorecard.delivery}
                    Data as of {asOf(scorecard.delivery.received_at)} for the two text columns.
                {:else}
                    No consultant delivery yet, so the text columns read 0.
                {/if}
                Registered, active, target, contacts and support come from this app's voter
                records, counted now.
            </p>
            {#if scorecardRows.length}
                <SortableTable
                    columns={SCORECARD_COLUMNS}
                    rows={scorecardRows}
                    defaultSort="registered" />
            {:else}
                <p class="empty">No voters loaded yet.</p>
            {/if}
        {/if}
    </section>

    <section class="block">
        <div class="block-head"><h2>Voting preference by age</h2></div>
        {#if preferenceError}
            <p class="msg msg-error">{preferenceError}</p>
        {:else if !preference}
            <p class="loading">Loading...</p>
        {:else if !preference.delivery}
            <p class="empty">{EMPTY}</p>
        {:else}
            <p class="as-of">
                Data as of {asOf(preference.delivery.received_at)}. Source: {preference.delivery
                    .filename}.
            </p>
            {#if chart4 && chart4.categories.length}
                <StackedBars
                    categories={chart4.categories}
                    series={chart4.series}
                    orientation="vertical"
                    valueLabel="Voters"
                    title="Voting preference by age" />
            {:else}
                <p class="empty">This delivery has no rows.</p>
            {/if}
        {/if}
    </section>

    <section class="block">
        <div class="block-head"><h2>Canvass activity</h2></div>
        {#if activityError}
            <p class="msg msg-error">{activityError}</p>
        {:else if !activity}
            <p class="loading">Loading...</p>
        {:else}
            <p class="as-of">
                Data as of {asOf(null)}. Monday-start UTC weeks, last 26 weeks, from this app's
                own contacts.
            </p>
            {#if noContacts}
                <p class="empty">No contacts recorded yet</p>
            {:else if chart5}
                <StackedBars
                    categories={chart5.categories}
                    series={chart5.series}
                    orientation="vertical"
                    valueLabel="Contacts"
                    title="Canvass activity" />
            {/if}
            <h3>Top issues, all time</h3>
            {#if activity.issues.length}
                <SortableTable columns={ISSUE_COLUMNS} rows={issueRows} defaultSort="count" />
            {:else}
                <p class="empty">No issue tags recorded yet.</p>
            {/if}

            <section aria-labelledby="write-ins-heading">
                <h3 id="write-ins-heading">Write-in issues</h3>
                {#if writeIns.rows.length}
                    <table class="write-ins">
                        <thead>
                            <tr>
                                <th scope="col">Date</th>
                                <th scope="col">Voter</th>
                                <th scope="col">Issue</th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- Keyed on the contact id, the row's own
                            identity: one voter can carry two write-ins at
                            the same instant, and a shared key throws. The
                            index fallback keeps a response cached from
                            before contact_id existed renderable. -->
                            {#each writeIns.rows as row, i (row.contact_id ?? i)}
                                <tr>
                                    <td class="nowrap">{asOf(row.created_at)}</td>
                                    <td>
                                        <a href="#/map?voter={row.voter_id}">
                                            {row.voter_name}
                                        </a>
                                    </td>
                                    <td>{row.text}</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                    {#if writeInsHidden > 0}
                        <p class="as-of">
                            Showing the {writeIns.rows.length} most recent of {writeIns.total}.
                        </p>
                    {/if}
                {:else}
                    <p class="empty">No write-in issues yet.</p>
                {/if}
            </section>
        {/if}
    </section>
</div>

<style>
    .admin-page {
        max-width: 1100px;
        margin: 0 auto;
        padding: var(--s-5) var(--s-4) var(--s-8);
    }
    .admin-header {
        margin-bottom: var(--s-5);
    }
    .page-sub {
        color: var(--ink-muted);
        margin: var(--s-2) 0 0;
        font-size: 0.88rem;
    }
    .block {
        margin-bottom: var(--s-7);
    }
    .write-ins {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
    }
    .write-ins th,
    .write-ins td {
        text-align: left;
        padding: var(--s-2);
        border-bottom: 1px solid var(--rule, #e5e5e5);
        vertical-align: top;
    }
    .write-ins .nowrap {
        white-space: nowrap;
    }
    .block-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        border-bottom: 1px solid var(--rule);
        padding-bottom: var(--s-2);
        margin-bottom: var(--s-3);
    }
    .as-of {
        color: var(--ink-muted);
        font-size: 0.76rem;
        margin: 0 0 var(--s-3);
    }
    .empty,
    .loading {
        color: var(--ink-muted);
        font-size: 0.86rem;
        margin: var(--s-3) 0;
    }
    .msg {
        margin: 0 0 var(--s-3);
        padding: 10px 14px;
        font-size: 0.9rem;
        border-left: 3px solid;
    }
    .msg-error {
        background: var(--alert-soft);
        border-color: var(--alert);
        color: var(--alert);
        font-size: 0.86rem;
    }
    h3 {
        margin: var(--s-5) 0 var(--s-2);
        font-size: 1rem;
    }
</style>
